import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Minus, Camera, Image, Calculator, Shield, FileText, ShoppingCart, CreditCard, CheckCircle, Loader2, Gift } from "lucide-react";
import { useCreateDeal } from "@/hooks/use-deals";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TermsAcknowledgment } from "@/components/common/TermsAcknowledgment";
import { ProposalAcceptanceSection } from "@/components/common/ProposalAcceptanceSection";

interface CustomerOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'create' | 'edit';
  initialData?: {
    dealId?: string;
    title?: string;
    orderType?: 'cameras_and_credits' | 'credits_only';
    cameraQuantity?: number;
    credits?: number;
    dealValue?: number;
    contactId?: string;
    organizationId?: string;
    cameraModelId?: string;
    plan?: string;
    paymentStructure?: 'annual' | 'upfront';
    commitmentYears?: 2 | 3 | 5;
    customAiModel?: boolean;
    discountAmount?: number;
    discountTypes?: string[];
    accessories?: Array<{ id: string; name: string; quantity: number; unit_price: number }>;
    deal_data?: any;
  };
  onAcceptProposal?: (dealId: string, deliveryAddressId: string | null, orderReference: string | null, updatedProposalData?: any) => void;
}

interface CameraVariant {
  id: string;
  name: string;
  price: number;
  description: string;
  hidden?: boolean;
}

const cameraVariants: CameraVariant[] = [
  {
    id: "ssc100",
    name: "Day Vision SSC100",
    price: 195,
    description: "Standard day vision camera",
    hidden: true
  },
  {
    id: "ssc200",
    name: "Day & Night Vision Camera SSC200",
    price: 295,
    description: "Enhanced day & night vision camera"
  }
];

const visibleCameraVariants = cameraVariants.filter(c => !c.hidden);

interface PlanTier {
  name: string;
  duration: number;
  minCredits: number;
  pricePerImage: number;
  description: string;
  color: string;
}

const TRIAL_CREDITS = 600;
const TRIAL_CAMERA_PRICE = 495;

const planTiers: PlanTier[] = [
  {
    name: "Trial",
    duration: 1,
    minCredits: 600,
    pricePerImage: 0,
    description: "Camera + 600 credits, continue or return",
    color: "bg-slate-100 border-slate-300"
  },
  {
    name: "Core", 
    duration: 3,
    minCredits: 1095,
    pricePerImage: 0.39,
    description: "3 images per day – ideal for standard monitoring",
    color: "bg-teal-50 border-teal-300"
  },
  {
    name: "Pro",
    duration: 3, 
    minCredits: 2190,
    pricePerImage: 0.35,
    description: "6 images per day – enhanced visibility",
    color: "bg-yellow-50 border-yellow-300"
  },
  {
    name: "Plus",
    duration: 5,
    minCredits: 2920,
    pricePerImage: 0.29,
    description: "8 images per day – maximum coverage",
    color: "bg-emerald-50 border-emerald-300"
  }
];

export const CustomerOrderDialog = ({ 
  open, 
  onOpenChange, 
  mode = 'create', 
  initialData, 
  onAcceptProposal 
}: CustomerOrderDialogProps) => {
  const { user, session } = useAuth();
  const createDeal = useCreateDeal();
  const { toast } = useToast();
  
  const [selectedCamera, setSelectedCamera] = useState<CameraVariant>(visibleCameraVariants[0]);
  const [orderType, setOrderType] = useState<'cameras_and_credits' | 'credits_only'>(
    initialData?.orderType || 'cameras_and_credits'
  );
  const [cameraQuantity, setCameraQuantity] = useState(initialData?.cameraQuantity || 1);
  const [credits, setCredits] = useState(initialData?.credits || 1095);
  const [selectedPlan, setSelectedPlan] = useState<PlanTier>(planTiers[1]);
  const [paymentType, setPaymentType] = useState<'annual' | 'upfront'>(initialData?.paymentStructure || 'annual');
  const [commitmentYears, setCommitmentYears] = useState<2 | 3 | 5>(initialData?.commitmentYears || 3);
  const [customAiModel, setCustomAiModel] = useState(Boolean(initialData?.customAiModel));
  const [discountAmount, setDiscountAmount] = useState(initialData?.discountAmount || 0);
  const [discountTypes, setDiscountTypes] = useState<string[]>(initialData?.discountTypes || []);
  const [proposalAccessories, setProposalAccessories] = useState<Array<{ id: string; name: string; quantity: number; unit_price: number }>>(initialData?.accessories || []);
  const [isCreatingProposal, setIsCreatingProposal] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [termsAcknowledged, setTermsAcknowledged] = useState(false);
  const [userOrganizationId, setUserOrganizationId] = useState<string | null>(
    initialData?.organizationId || null
  );
  const [acceptanceReady, setAcceptanceReady] = useState(false);
  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState<string | null>(null);
  const [selectedOrderReference, setSelectedOrderReference] = useState<string | null>(null);

  // Fetch user's organization ID
  useEffect(() => {
    const fetchUserOrganization = async () => {
      if (!user?.id) return;
      
      try {
        const { data: userRole } = await supabase
          .from('user_roles')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        if (userRole?.organization_id) {
          setUserOrganizationId(userRole.organization_id);
        }
      } catch (error) {
        console.error("Error fetching user organization:", error);
      }
    };

    fetchUserOrganization();
  }, [user?.id]);

  // Load data from existing deal if in edit mode
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      const savedPlan = planTiers.find(plan => plan.name === initialData.plan) || planTiers[1];
      const savedCamera = visibleCameraVariants.find(camera => camera.id === initialData.cameraModelId) || visibleCameraVariants[0];

      setSelectedCamera(savedCamera);
      setOrderType(initialData.orderType || 'cameras_and_credits');
      setCameraQuantity(Math.max(1, Number(initialData.cameraQuantity) || 1));
      setSelectedPlan(savedPlan);
      setCredits(Number(initialData.credits) || savedPlan.minCredits);
      setPaymentType(initialData.paymentStructure === 'upfront' ? 'upfront' : 'annual');
      setCommitmentYears(initialData.commitmentYears === 5 ? 5 : initialData.commitmentYears === 2 ? 2 : 3);
      setCustomAiModel(Boolean(initialData.customAiModel));
      setDiscountAmount(Number(initialData.discountAmount) || 0);
      setDiscountTypes(Array.isArray(initialData.discountTypes) ? initialData.discountTypes : []);
      setProposalAccessories(Array.isArray(initialData.accessories) ? initialData.accessories : []);
    }
  }, [mode, initialData]);

  const handlePlanSelect = (plan: PlanTier) => {
    setSelectedPlan(plan);
    setCredits(plan.minCredits);
    if (plan.name !== 'Trial' && plan.duration === 5) setCommitmentYears(5);
    if (plan.name !== 'Trial' && plan.duration !== 5) setCommitmentYears(3);
    
    toast({
      title: `${plan.name} Plan Selected!`,
      description: plan.description,
    });
  };

  // Reset form when order type changes
  useEffect(() => {
    if (mode !== 'edit' && orderType === 'credits_only') {
      setCameraQuantity(1); // Internal value, not displayed
      setCredits(1095); // Reset to Core minimum
    }
  }, [mode, orderType]);

  // Business logic calculations - mirror staff DealCalculator packages
  const isTrial = selectedPlan.name === 'Trial';
  const cameraPrice = isTrial ? TRIAL_CAMERA_PRICE : (commitmentYears === 5 ? 0 : selectedCamera.price);
  const effectiveCameraQuantity = orderType === 'cameras_and_credits' ? cameraQuantity : 0;
  const creditsPerCamera = isTrial ? TRIAL_CREDITS : credits;
  const totalCreditsNeeded = orderType === 'cameras_and_credits'
    ? creditsPerCamera * cameraQuantity
    : creditsPerCamera;
  const basePricePerImage = isTrial ? 0 : selectedPlan.pricePerImage + (customAiModel ? 0.10 : 0);
  const hasImageDiscount = !isTrial && discountTypes.includes('image_4p');
  const pricePerImage = hasImageDiscount ? Math.max(0, basePricePerImage - 0.04) : basePricePerImage;
  const cameraCost = effectiveCameraQuantity * cameraPrice;
  const originalCameraUnitPrice = Number(initialData?.deal_data?.camera_unit_price ?? initialData?.deal_data?.camera_price ?? selectedCamera.price) || selectedCamera.price;
  const savedCameraCostBeforeDiscount = effectiveCameraQuantity * originalCameraUnitPrice;
  const accessoriesCost = proposalAccessories.reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unit_price) || 0), 0);
  const cameraDiscountAmount = isTrial
    ? (discountTypes.includes('essential_100') ? cameraCost : 0)
    : discountTypes.includes('camera_100')
      ? (cameraCost > 0 ? cameraCost : savedCameraCostBeforeDiscount)
      : discountTypes.includes('camera_50')
        ? (cameraCost > 0 ? cameraCost : savedCameraCostBeforeDiscount) * 0.5
        : discountTypes.includes('camera_10')
          ? effectiveCameraQuantity * 10
          : 0;
  // Annual contract calculations
  const coverageYears = isTrial ? 1 : commitmentYears;
  const annualCredits = totalCreditsNeeded;
  const year1CreditsCost = isTrial ? 0 : annualCredits * pricePerImage;
  const creditsCost = year1CreditsCost * coverageYears;
  const year1Subtotal = cameraCost + year1CreditsCost + accessoriesCost;
  const subtotalBeforeDiscount = isTrial
    ? cameraCost + accessoriesCost
    : paymentType === 'annual'
      ? year1Subtotal
      : creditsCost + cameraCost + accessoriesCost;
  // Use different subtotal based on payment type
  const total = Math.max(0, isTrial
    ? cameraCost + accessoriesCost - cameraDiscountAmount
    : paymentType === 'annual'
      ? year1Subtotal - cameraDiscountAmount
      : creditsCost + cameraCost + accessoriesCost - cameraDiscountAmount);
  const paymentSummaryDiscount = Math.max(0, subtotalBeforeDiscount - total);
  const showDiscountNotice = mode === 'edit' && paymentSummaryDiscount > 0;
  const totalDays = 365 * coverageYears;
  const shotsPerDay = isTrial ? Math.floor(TRIAL_CREDITS / 365) : Math.round(credits / 365);
  const imageCostPerDay = isTrial ? 0 : pricePerImage * shotsPerDay;
  const cameraCostPerDay = cameraPrice / totalDays;
  const averageCostPerDay = imageCostPerDay + cameraCostPerDay;
  const isFleetMode = effectiveCameraQuantity >= 25;
  const formatGBP = (amount: number) => `£${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const handleQuantityChange = (delta: number) => {
    const newQuantity = Math.max(1, cameraQuantity + delta);
    setCameraQuantity(newQuantity);
  };

  const handleCreditsChange = (value: number[]) => {
    const newCredits = value[0];
    setCredits(newCredits);
  };

  const sendNotification = async (dealTitle: string, dealType: string, dealId: string) => {
    try {
      // Get user's profile for assignee information
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (!profile) return;

      const notificationData = {
        dealId,
        contactEmail: profile.email,
        contactName: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Customer',
        dealTitle,
        totalCameras: effectiveCameraQuantity,
        totalImages: totalCreditsNeeded,
        dealValue: total,
        organizationName: '',
        assigneeName: '',
        assigneeType: '',
        assigneeEmail: '',
        dealType, // 'proposal' or 'order'
        customerGenerated: true, // Flag to indicate customer-initiated
        autoAccepted: dealType === 'order' // Auto-accept for direct orders
      };

      await supabase.functions.invoke('send-proposal-notification', {
        body: notificationData
      });
    } catch (error) {
      console.error("Error sending notification:", error);
      // Don't block the deal creation if notification fails
    }
  };

  const getOrderTitle = () => {
    if (orderType === 'credits_only') {
      return `${totalCreditsNeeded.toLocaleString()} Credits (${selectedPlan.name} Plan)`;
    }
    return `${cameraQuantity}x Camera${cameraQuantity > 1 ? 's' : ''} + ${totalCreditsNeeded.toLocaleString()} Credits (${selectedPlan.name} Plan)`;
  };

  const handleSaveAsProposal = async () => {
    if (!user || !session) {
      toast({
        title: "Error",
        description: "You must be logged in to create a proposal",
        variant: "destructive"
      });
      return;
    }

    if (!termsAcknowledged) {
      toast({
        title: "Terms Required",
        description: "Please acknowledge the terms and conditions before creating a proposal",
        variant: "destructive"
      });
      return;
    }

    setIsCreatingProposal(true);

    try {
      // Get the Opportunity stage ID
      const { data: stages } = await supabase
        .from('crm_deal_stages')
        .select('id, name')
        .eq('name', 'Opportunity')
        .single();

      // Get the current user's profile to use as contact
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) {
        throw new Error("User profile not found");
      }

      const dealData = {
        title: getOrderTitle(),
        contact_id: profile.id,
        organization_id: userOrganizationId || undefined,
        value: total,
        probability: 50,
        stage_id: stages?.id || undefined,
        status: 'draft',
        total_cameras: effectiveCameraQuantity,
        total_images: totalCreditsNeeded,
        total_revenue: total,
        image_revenue: creditsCost,
        deal_data: {
          customer_initiated: true,
          self_service_type: 'proposal',
          order_type: orderType,
          camera_model: selectedCamera.name,
          camera_model_id: selectedCamera.id,
          camera_unit_price: cameraPrice,
          plan: selectedPlan.name,
          credits_per_camera: credits,
          price_per_image: pricePerImage,
          camera_cost: cameraCost,
          credits_cost: creditsCost,
          coverage_years: coverageYears,
          commitment_years: isTrial ? 1 : commitmentYears,
          shots_per_day: shotsPerDay,
          created_by_customer: user.id,
          payment_structure: paymentType,
          annual_credits: paymentType === 'annual' ? annualCredits : undefined,
          year1_credits_cost: paymentType === 'annual' ? year1CreditsCost : undefined,
          custom_ai_model: customAiModel,
          discount_amount: discountAmount,
          discount_types: discountTypes,
          accessories: proposalAccessories,
          accessories_cost: accessoriesCost
        }
      };

      const createdDeal = await createDeal.mutateAsync(dealData);
      
      // Send notification about new proposal
      await sendNotification(dealData.title, 'proposal', createdDeal.id);

      onOpenChange(false);
      // Reset form
      setCameraQuantity(1);
      setCredits(1095);
      setOrderType('cameras_and_credits');
      setPaymentType('annual');
      setTermsAcknowledged(false);
      
      toast({
        title: "Proposal Saved",
        description: "Your proposal has been saved and is now available for review and editing",
      });
    } catch (error) {
      console.error("Error creating proposal:", error);
      toast({
        title: "Error",
        description: "Failed to save proposal. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCreatingProposal(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!user || !session) {
      toast({
        title: "Error",
        description: "You must be logged in to create an order",
        variant: "destructive"
      });
      return;
    }

    if (!termsAcknowledged) {
      toast({
        title: "Terms Required",
        description: "Please acknowledge the terms and conditions before creating an order",
        variant: "destructive"
      });
      return;
    }

    setIsCreatingOrder(true);

    try {
      // Get the current user's profile to use as contact
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) {
        throw new Error("User profile not found");
      }

      const dealData = {
        title: getOrderTitle(),
        contact_id: profile.id,
        organization_id: userOrganizationId || undefined,
        value: total,
        probability: 100,
        stage_id: null, // Won deals should have no stage
        status: 'won',
        total_cameras: effectiveCameraQuantity,
        total_images: totalCreditsNeeded,
        total_revenue: total,
        image_revenue: creditsCost,
        deal_data: {
          customer_initiated: true,
          self_service_type: 'order',
          order_type: orderType,
          camera_model: selectedCamera.name,
          camera_model_id: selectedCamera.id,
          camera_unit_price: cameraPrice,
          plan: selectedPlan.name,
          credits_per_camera: credits,
          price_per_image: pricePerImage,
          camera_cost: cameraCost,
          credits_cost: creditsCost,
          coverage_years: coverageYears,
          commitment_years: isTrial ? 1 : commitmentYears,
          shots_per_day: shotsPerDay,
          created_by_customer: user.id,
          payment_structure: paymentType,
          annual_credits: paymentType === 'annual' ? annualCredits : undefined,
          year1_credits_cost: paymentType === 'annual' ? year1CreditsCost : undefined,
          custom_ai_model: customAiModel,
          discount_amount: discountAmount,
          discount_types: discountTypes,
          accessories: proposalAccessories,
          accessories_cost: accessoriesCost
        }
      };

      const createdDeal = await createDeal.mutateAsync(dealData);
      
      // Send notification about new order
      await sendNotification(dealData.title, 'order', createdDeal.id);

      onOpenChange(false);
      // Reset form
      setCameraQuantity(1);
      setCredits(1095);
      setOrderType('cameras_and_credits');
      setPaymentType('annual');
      setTermsAcknowledged(false);
      
      toast({
        title: "Order Created",
        description: "Your order has been created and submitted for processing",
      });
    } catch (error) {
      console.error("Error creating order:", error);
      toast({
        title: "Error",
        description: "Failed to create order. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCreatingOrder(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-slate-50 border-slate-200">
        <DialogHeader className="sticky top-0 z-20 bg-component-background -mx-6 -mt-6 px-6 py-4 rounded-t-xl border-b border-foreground text-foreground">
          <DialogTitle className="text-xl font-semibold text-foreground">
            {mode === 'edit' ? 'Edit Proposal' : 'Order Calculator'}
          </DialogTitle>
          <p className="text-sm text-foreground">
            {mode === 'edit' 
              ? 'Review and modify your proposal, then accept when ready.'
              : 'Create your own custom order proposal with cameras and image packages tailored to your needs.'
            }
          </p>
          {showDiscountNotice && (
            <div className="mt-3 rounded-lg border-2 border-success bg-success/20 p-3 text-success-foreground">
              <div className="flex items-start gap-2">
                <Gift className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div className="space-y-1">
                  <div className="text-sm font-bold">
                    Discount applied to this proposal: −{formatGBP(paymentSummaryDiscount)}
                  </div>
                  <div className="text-xs font-medium opacity-90">
                    This is already included in the total shown below.
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Left Column - Calculator */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Type Selection */}
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShoppingCart className="h-5 w-5" />
                  Order Type
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Tabs value={orderType} onValueChange={(value) => setOrderType(value as 'cameras_and_credits' | 'credits_only')}>
                  <TabsList className="grid w-full grid-cols-2 bg-slate-100">
                    <TabsTrigger value="cameras_and_credits" className="data-[state=active]:bg-lime-button data-[state=active]:text-black">
                      Cameras + Credits
                    </TabsTrigger>
                    <TabsTrigger value="credits_only" className="data-[state=active]:bg-lime-button data-[state=active]:text-black">
                      Credits Only
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="cameras_and_credits" className="mt-4">
                    <div className="text-sm text-slate-600">
                      Purchase cameras along with image credits for a complete solution.
                    </div>
                  </TabsContent>
                  <TabsContent value="credits_only" className="mt-4">
                    <div className="text-sm text-slate-600">
                      Purchase only image credits for use with your existing cameras.
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Payment Structure Selection */}
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="h-5 w-5" />
                  Payment Structure
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Tabs value={paymentType} onValueChange={(value) => setPaymentType(value as 'annual' | 'upfront')}>
                  <TabsList className="grid w-full grid-cols-2 bg-slate-100">
                    <TabsTrigger value="annual" className="flex items-center gap-2 data-[state=active]:bg-lime-button data-[state=active]:text-black">
                      <Shield className="h-4 w-4" />
                      Annual Contract
                    </TabsTrigger>
                    <TabsTrigger value="upfront" className="flex items-center gap-2 data-[state=active]:bg-lime-button data-[state=active]:text-black">
                      <CreditCard className="h-4 w-4" />
                      Upfront Payment
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="annual" className="mt-4">
                    <div className="text-sm text-slate-600">
                      Spread payments over {coverageYears} years with annual commitments. Lower Year 1 payment includes cameras + first year credits.
                    </div>
                  </TabsContent>
                  <TabsContent value="upfront" className="mt-4">
                    <div className="text-sm text-slate-600">
                      Single payment for full {coverageYears}-year coverage. All credits allocated immediately.
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Camera Model Selection - Only show for cameras_and_credits */}
            {orderType === 'cameras_and_credits' && (
              <Card className="border-slate-200">
                <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Camera className="h-5 w-5" />
                    Camera Model
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {visibleCameraVariants.map((camera) => (
                      <div
                        key={camera.id}
                        onClick={() => {
                          setSelectedCamera(camera);
                          toast({
                            title: `${camera.name} Selected!`,
                            description: camera.description,
                          });
                        }}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedCamera.id === camera.id
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-slate-200 hover:border-teal-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-slate-800">{camera.name}</h4>
                            <p className="text-sm text-slate-600 mt-1">{camera.description}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-teal-600">£{camera.price}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Camera Quantity - Only show for cameras_and_credits */}
            {orderType === 'cameras_and_credits' && (
              <Card className="border-slate-200">
                <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Camera className="h-5 w-5" />
                    Camera Quantity
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={cameraQuantity <= 1}
                      className="h-10 w-10 rounded-full border-slate-300"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    
                    <div className="text-center">
                      <Input
                        type="number"
                        min={1}
                        value={cameraQuantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          setCameraQuantity(Math.max(1, value));
                        }}
                        onBlur={(e) => {
                          const value = parseInt(e.target.value);
                          if (isNaN(value) || value < 1) {
                            setCameraQuantity(1);
                          }
                        }}
                        className="text-3xl font-bold text-slate-800 w-24 text-center mb-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <div className="text-sm text-slate-600">Camera{cameraQuantity > 1 ? 's' : ''}</div>
                      <div className="text-lg font-semibold text-teal-600">£{cameraPrice} each</div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleQuantityChange(1)}
                      className="h-10 w-10 rounded-full border-slate-300"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {isFleetMode && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-amber-600" />
                        <span className="font-medium text-amber-800">Fleet Mode Active</span>
                      </div>
                      <p className="text-sm text-amber-700 mt-1">
                        You're ordering 25+ cameras. Contact our team for volume discounts and dedicated support.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Plan Tiers */}
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="h-5 w-5" />
                  Plan Tiers
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {planTiers.map((plan) => (
                    <div
                      key={plan.name}
                      onClick={() => handlePlanSelect(plan)}
                      className={`
                        p-4 rounded-lg border-2 cursor-pointer transition-all hover:scale-105 hover:shadow-md
                        ${plan.color}
                        ${selectedPlan.name === plan.name ? 'ring-2 ring-teal-500' : ''}
                      `}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-semibold text-slate-800">{plan.name}</div>
                        <Badge variant="outline" className="text-xs">
                          {plan.name === 'Trial' ? 'Trial' : `${plan.duration} year${plan.duration > 1 ? 's' : ''}`}
                        </Badge>
                      </div>
                      <div className="text-sm text-slate-600 mb-3">{plan.description}</div>
                      <div className="space-y-1">
                        <div className="text-lg font-bold text-slate-800">
                          £{plan.pricePerImage}/image
                        </div>
                        <div className="text-xs text-slate-600">
                          Min: {plan.minCredits.toLocaleString()} credits
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Credit Slider */}
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Image className="h-5 w-5" />
                  Credits per Camera
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <Label className="text-lg font-medium text-slate-700">Credits</Label>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-slate-800">{credits.toLocaleString()}</div>
                      <div className="text-sm text-slate-600">
                        {orderType === 'cameras_and_credits' ? 'per camera' : 'total'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="px-3">
                    <Slider
                      value={[credits]}
                      onValueChange={handleCreditsChange}
                      min={365}
                      max={10000}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-2">
                      <span>365</span>
                      <span>10,000</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="font-medium text-blue-800">Daily Coverage</div>
                      <div className="text-blue-600">{shotsPerDay} images/day</div>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="font-medium text-green-800">Plan Duration</div>
                      <div className="text-green-600">{coverageYears} year{coverageYears > 1 ? 's' : ''}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Terms and Conditions - Only show in create mode */}
            {mode !== 'edit' && (
              <Card className="border-slate-200">
                <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="h-5 w-5" />
                    Terms & Conditions
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <TermsAcknowledgment
                    onAcknowledgmentChange={setTermsAcknowledged}
                    className="text-sm"
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <Card className="border-slate-200 shadow-lg">
                <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Calculator className="h-5 w-5" />
                    Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* Plan Badge */}
                  <div className={`p-3 rounded-lg ${selectedPlan.color}`}>
                    <div className="font-semibold text-slate-800">{selectedPlan.name} Plan</div>
                    <div className="text-sm text-slate-600">{selectedPlan.description}</div>
                    <div className="text-lg font-bold text-slate-800 mt-1">
                      £{selectedPlan.pricePerImage}/image
                    </div>
                  </div>

                  {/* Order Details */}
                  <div className="space-y-3 border-t border-slate-200 pt-4">
                    {paymentType === 'annual' ? (
                      <>
                        {/* Annual Contract Breakdown */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                          <div className="text-sm font-semibold text-blue-800 mb-2">Annual Contract - {coverageYears} Year Term</div>
                          <div className="text-xs text-blue-600">Minimum commitment: {coverageYears} years</div>
                        </div>

                        <div className="space-y-2">
                          <div className="font-semibold text-slate-800 border-b border-slate-200 pb-2">Year 1 Payment</div>
                          
                          {effectiveCameraQuantity > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">{cameraQuantity}x Camera{cameraQuantity > 1 ? 's' : ''}</span>
                              <span className="font-semibold text-slate-800">£{cameraCost.toLocaleString()}</span>
                            </div>
                          )}
                          
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Year 1 Credits ({annualCredits.toLocaleString()})</span>
                            <span className="font-semibold text-slate-800">£{year1CreditsCost.toFixed(2)}</span>
                          </div>

                          <div className="border-t border-slate-200 pt-2">
                            {proposalAccessories.length > 0 && (
                              <div className="space-y-1">
                                <div className="text-sm font-medium text-slate-800">Accessories</div>
                                {proposalAccessories.map((item, index) => {
                                  const quantity = Number(item.quantity) || 0;
                                  const unitPrice = Number(item.unit_price) || 0;
                                  const lineTotal = quantity * unitPrice;

                                  return (
                                    <div key={item.id || `${item.name}-${index}`} className="flex justify-between gap-3 text-sm">
                                      <span className="text-slate-600">
                                        {item.name || 'Accessory'} × {quantity} @ £{unitPrice.toFixed(2)}
                                      </span>
                                      <span className="shrink-0 text-slate-800">£{lineTotal.toFixed(2)}</span>
                                    </div>
                                  );
                                })}
                                <div className="flex justify-between text-sm font-medium">
                                  <span className="text-slate-600">Accessories Subtotal</span>
                                  <span className="text-slate-800">£{accessoriesCost.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Year 1 Subtotal</span>
                              <span className="text-slate-800">£{year1Subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-bold">
                              <span className="text-slate-800">Year 1 Total</span>
                              <span className="text-slate-800">£{total.toFixed(2)}</span>
                            </div>
                            <div className="text-xs text-slate-500 text-center mt-2">
                              All amounts are ex VAT
                            </div>
                          </div>
                        </div>

                        {coverageYears > 1 && (
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                            <div className="text-sm font-semibold text-slate-800 mb-2">Subsequent Years ({coverageYears - 1} years)</div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Annual Credits ({annualCredits.toLocaleString()})</span>
                              <span className="font-semibold text-slate-800">£{year1CreditsCost.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-bold border-t border-slate-300 pt-2">
                              <span className="text-slate-800">Annual Payment</span>
                              <span className="text-slate-800">£{year1CreditsCost.toFixed(2)}</span>
                            </div>
                            <div className="text-xs text-slate-500 text-center mt-2">
                              All amounts are ex VAT
                            </div>
                          </div>
                        )}

                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                          <div className="text-sm font-semibold text-teal-800">Total Contract Value</div>
                          <div className="text-lg font-bold text-teal-800">
                            £{(total + (coverageYears - 1) * year1CreditsCost).toFixed(2)}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Upfront Payment Breakdown */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                          <div className="text-sm font-semibold text-green-800 mb-2">Upfront Payment - {coverageYears} Year Coverage</div>
                          <div className="text-xs text-green-600">All credits allocated immediately, no annual commitments</div>
                        </div>

                        {effectiveCameraQuantity > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">{cameraQuantity}x Camera{cameraQuantity > 1 ? 's' : ''}</span>
                            <span className="font-semibold text-slate-800">£{cameraCost.toLocaleString()}</span>
                          </div>
                        )}
                        
                        <div className="flex justify-between">
                          <span className="text-slate-600">{totalCreditsNeeded.toLocaleString()} Credits (Full {coverageYears} year{coverageYears > 1 ? 's' : ''})</span>
                          <span className="font-semibold text-slate-800">£{creditsCost.toFixed(2)}</span>
                        </div>

                        <div className="border-t border-slate-200 pt-3">
                          {proposalAccessories.length > 0 && (
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-slate-800">Accessories</div>
                              {proposalAccessories.map((item, index) => {
                                const quantity = Number(item.quantity) || 0;
                                const unitPrice = Number(item.unit_price) || 0;
                                const lineTotal = quantity * unitPrice;

                                return (
                                  <div key={item.id || `${item.name}-${index}`} className="flex justify-between gap-3 text-sm">
                                    <span className="text-slate-600">
                                      {item.name || 'Accessory'} × {quantity} @ £{unitPrice.toFixed(2)}
                                    </span>
                                    <span className="shrink-0 text-slate-800">£{lineTotal.toFixed(2)}</span>
                                  </div>
                                );
                              })}
                              <div className="flex justify-between text-sm font-medium">
                                <span className="text-slate-600">Accessories Subtotal</span>
                                <span className="text-slate-800">£{accessoriesCost.toFixed(2)}</span>
                              </div>
                            </div>
                          )}
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Subtotal</span>
                            <span className="text-slate-800">£{(creditsCost + cameraCost + accessoriesCost).toFixed(2)}</span>
                          </div>
                          <div className="text-xs text-slate-500 text-center mt-2">
                            All amounts are ex VAT
                          </div>
                        </div>

                        <div className="border-t border-slate-200 pt-3">
                          <div className="flex justify-between text-lg font-bold">
                            <span className="text-slate-800">Total Upfront Payment</span>
                            <span className="text-teal-600">£{total.toFixed(2)}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Cost Analysis */}
                  <div className="bg-slate-50 p-3 rounded-lg border-t border-slate-200">
                    <div className="text-sm font-medium text-slate-700 mb-2">Cost Analysis</div>
                    <div className="space-y-1 text-sm text-slate-600">
                      <div className="flex justify-between">
                        <span>Coverage</span>
                        <span>{coverageYears} year{coverageYears > 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Daily cost</span>
                        <span>£{averageCostPerDay.toFixed(2)}/day</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Images per day</span>
                        <span>{shotsPerDay}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-4">
                    {mode === 'edit' && onAcceptProposal ? (
                       <ProposalAcceptanceSection
                        organizationId={userOrganizationId && userOrganizationId !== '00000000-0000-0000-0000-000000000000' ? userOrganizationId : undefined}
                         onAcceptanceReady={(ready, deliveryAddressId, orderReference) => {
                           setAcceptanceReady(ready);
                           setSelectedDeliveryAddressId(deliveryAddressId);
                           setSelectedOrderReference(orderReference);
                         }}
                         isPilot={selectedPlan.name === 'Trial'}
                      />
                    ) : (
                      <TermsAcknowledgment 
                        onAcknowledgmentChange={setTermsAcknowledged}
                        variant={selectedPlan.name === 'Trial' ? 'pilot' : 'standard'}
                      />
                    )}
                    
                    <div className="flex gap-3">
                      {mode === 'edit' && onAcceptProposal ? (
                        <Button
                         onClick={() => {
                             if (initialData?.dealId && selectedDeliveryAddressId) {
                               // Collect current proposal data
                               const updatedProposalData = {
                                 title: getOrderTitle(),
                                 value: total,
                                 total_cameras: effectiveCameraQuantity,
                                 total_images: totalCreditsNeeded,
                                 total_revenue: total,
                                 image_revenue: creditsCost,
                  deal_data: {
                    ...(initialData?.deal_data || {}),
                    customer_initiated: true,
                    self_service_type: 'proposal',
                    order_type: orderType,
                    camera_model: selectedCamera.name,
                    camera_model_id: selectedCamera.id,
                    camera_unit_price: cameraPrice,
                    plan: selectedPlan.name,
                    credits_per_camera: credits,
                    price_per_image: pricePerImage,
                    camera_cost: cameraCost,
                    credits_cost: creditsCost,
                                   coverage_years: coverageYears,
                                   commitment_years: isTrial ? 1 : commitmentYears,
                                   shots_per_day: shotsPerDay,
                                   payment_structure: paymentType,
                                   annual_credits: paymentType === 'annual' ? annualCredits : undefined,
                                   year1_credits_cost: paymentType === 'annual' ? year1CreditsCost : undefined,
                                   custom_ai_model: customAiModel,
                                   discount_amount: discountAmount,
                                   discount_types: discountTypes,
                                   accessories: proposalAccessories,
                                   accessories_cost: accessoriesCost
                                 },
                                 order_data: {
                                   order_type: orderType,
                                   total_cameras: effectiveCameraQuantity,
                                   total_images: totalCreditsNeeded,
                                   skip_device_allocation: false
                                 }
                               };
                               onAcceptProposal(initialData.dealId, selectedDeliveryAddressId, selectedOrderReference, updatedProposalData);
                             }
                          }}
                          disabled={!acceptanceReady || isCreatingProposal}
                          className="flex-1 bg-lime-button text-black hover:bg-lime-button/90"
                        >
                          {isCreatingProposal ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Accepting...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Accept Proposal
                            </>
                          )}
                        </Button>
                      ) : (
                        <>
                          <Button
                            onClick={handleSaveAsProposal}
                            disabled={!termsAcknowledged || isCreatingProposal}
                            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                          >
                            {isCreatingProposal ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <FileText className="h-4 w-4 mr-2" />
                                Save as Proposal
                              </>
                            )}
                          </Button>
                          
                          <Button
                            onClick={handleCreateOrder}
                            disabled={!termsAcknowledged || isCreatingOrder}
                            className="flex-1 bg-lime-button text-black hover:bg-lime-button/90"
                          >
                            {isCreatingOrder ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Creating...
                              </>
                            ) : (
                              <>
                                <CreditCard className="h-4 w-4 mr-2" />
                                Create Order
                              </>
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};